import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 20000,
  withCredentials: true,
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ---- Content ----
export const fetchFeatured = async (city) =>
  (await api.get("/featured", { params: city && city !== "All" ? { city } : {} })).data;
export const fetchRestaurants = async (params = {}) => (await api.get("/restaurants", { params })).data;
export const fetchRestaurant = async (id) => (await api.get(`/restaurants/${id}`)).data;
export const claimRestaurant = async (id) => (await api.post(`/restaurants/${id}/claim`)).data;
export const updateRestaurant = async (id, payload) => (await api.put(`/restaurants/${id}`, payload)).data;
export const fetchMyListings = async () => (await api.get("/me/listings")).data;
export const fetchArticles = async (params = {}) => (await api.get("/articles", { params })).data;
export const fetchArticle = async (id) => (await api.get(`/articles/${id}`)).data;
export const fetchEvents = async (params = {}) => (await api.get("/events", { params })).data;
export const fetchEvent = async (id) => (await api.get(`/events/${id}`)).data;
export const fetchHostEventTypes = async () => (await api.get("/host/event-types")).data;
export const createHostEvent = async (payload) => (await api.post("/host/events", payload)).data;
export const uploadEventImage = async (formData) =>
  (await api.post("/host/events/upload-image", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  })).data;
export const eventImageUrl = (path) => `${BACKEND_URL}${path}`;
export const updateHostEvent = async (id, payload) => (await api.put(`/host/events/${id}`, payload)).data;
export const deleteHostEvent = async (id) => (await api.delete(`/host/events/${id}`)).data;
export const fetchMyEvents = async () => (await api.get("/me/events")).data;
export const fetchMyRsvp = async (id) => (await api.get(`/events/${id}/rsvp/me`)).data;
export const rsvpEvent = async (id) => (await api.post(`/events/${id}/rsvp`)).data;
export const cancelRsvp = async (id) => (await api.delete(`/events/${id}/rsvp`)).data;
export const fetchEventAttendees = async (id) => (await api.get(`/host/events/${id}/attendees`)).data;
export const exportEventAttendeesCsv = async (id) => (await api.get(`/host/events/${id}/attendees/export`, { responseType: "blob" })).data;
export const submitBusinessInquiry = async (payload) => (await api.post("/business-inquiries", payload)).data;
export const subscribeNewsletter = async (email) => (await api.post("/newsletter", { email })).data;
export const fetchCities = async () => (await api.get("/cities")).data;

// ---- Auth ----
export const authRegister = async (payload) => (await api.post("/auth/register", payload)).data;
export const authLogin = async (payload) => (await api.post("/auth/login", payload)).data;
export const authLogout = async () => (await api.post("/auth/logout")).data;
export const authMe = async () => (await api.get("/auth/me")).data;
export const authGoogleSession = async (session_id) => (await api.post("/auth/session", { session_id })).data;

// ---- Profiles ----
export const fetchProfiles = async (params = {}) => (await api.get("/profiles", { params })).data;
export const fetchProfile = async (id) => (await api.get(`/profiles/${id}`)).data;
export const fetchProfileTypes = async () => (await api.get("/profile-types")).data;
export const createProfile = async (payload) => (await api.post("/profiles", payload)).data;
export const updateProfile = async (id, payload) => (await api.put(`/profiles/${id}`, payload)).data;
export const deleteProfile = async (id) => (await api.delete(`/profiles/${id}`)).data;
export const fetchMyProfiles = async () => (await api.get("/me/profiles")).data;
export const fetchMyInquiries = async () => (await api.get("/me/inquiries")).data;

// ---- Views & Analytics ----
export const trackRestaurantView = async (id) => { try { await api.post(`/restaurants/${id}/view`); } catch { /* noop */ } };
export const trackProfileView = async (id) => { try { await api.post(`/profiles/${id}/view`); } catch { /* noop */ } };

// ---- Admin ----
export const fetchVerificationQueue = async () => (await api.get("/admin/verification-queue")).data;
export const verifyListing = async (id, verified) => (await api.put(`/admin/listings/${id}/verify`, { verified })).data;
export const verifyProfile = async (id, verified) => (await api.put(`/admin/profiles/${id}/verify`, { verified })).data;
export const fetchSubscribers = async () => (await api.get("/admin/subscribers")).data;
export const exportSubscribersCsv = async () => (await api.get("/admin/subscribers/export", { responseType: "blob" })).data;
export const fetchPendingEvents = async () => (await api.get("/admin/events/pending")).data;
export const moderateEvent = async (id, status) => (await api.put(`/admin/events/${id}/moderate`, { status })).data;

// ---- Reviews ----
export const fetchReviews = async (entity_type, entity_id) => (await api.get("/reviews", { params: { entity_type, entity_id } })).data;
export const fetchReviewAggregates = async () => (await api.get("/reviews/aggregates")).data;

// ---- Videos ----
export const BRAND_VIDEO_POSTER = "https://static.prod-images.emergentagent.com/jobs/d86f81c4-d18b-4833-aa03-151c81172f9d/images/8d85acebf973702468fba63b8e9fd80a6b1ec288b82e513f4096c93918aed100.png";
export const fetchVideos = async (params = {}) => (await api.get("/videos", { params })).data;
export const fetchFeaturedVideos = async () => (await api.get("/videos/featured")).data;
export const fetchTrendingVideos = async (limit = 8) => (await api.get("/videos/trending", { params: { limit } })).data;
export const trackVideoView = async (id) => { try { return (await api.post(`/videos/${id}/view`)).data.views; } catch { return null; } };
export const createEmbedVideo = async (payload) => (await api.post("/videos", payload)).data;
export const uploadVideo = async (formData, onProgress) =>
  (await api.post("/videos/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000,
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  })).data;
export const deleteVideo = async (id) => (await api.delete(`/videos/${id}`)).data;
export const streamUrl = (id) => `${API}/videos/stream/${id}`;
export const fetchAdminVideos = async () => (await api.get("/admin/videos")).data;
export const setVideoFeatured = async (id, featured) => (await api.put(`/admin/videos/${id}/feature`, { featured })).data;
export const submitReview = async (payload) => (await api.post("/reviews", payload)).data;
export const deleteReview = async (id) => (await api.delete(`/reviews/${id}`)).data;
